import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/apiConfig';
import { FaEye, FaEyeSlash, FaLock } from 'react-icons/fa';

const ChangePassword = ({ role = 'user', onSuccess = null }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const getTokenForRole = () => {
    try {
      if (role === 'vendor') return localStorage.getItem('vendorToken');
      if (role === 'driver') return localStorage.getItem('driverToken') || localStorage.getItem('token');
      if (role === 'admin') return localStorage.getItem('adminToken') || (JSON.parse(localStorage.getItem('user')) || {}).token;
      // default: user
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      return user?.token || localStorage.getItem('token');
    } catch (e) {
      return null;
    }
  };

  const getEndpoint = () => {
    // API_URL is base like http://localhost:5000/api
    if (role === 'vendor') return `${API_URL}/vendor/updatepassword`;
    if (role === 'driver') return `${API_URL}/driver/auth/updatepassword`;
    // admin and user share auth endpoints
    return `${API_URL}/auth/updatepassword`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill all fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirm password do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    const token = getTokenForRole();
    // If token missing, do not attempt authenticated change here — guide user to forgot password
    if (!token) {
      toast.error('Authentication token not found. Please login or use Forgot Password to reset your password.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.put(
        getEndpoint(),
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data && (res.data.success || res.data.status)) {
        toast.success(res.data.message || 'Password updated successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        if (onSuccess) onSuccess();
      } else {
        const message = res.data?.message || 'Failed to update password';
        toast.error(message);
      }
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const token = getTokenForRole();
  if (!token) {
    return (
      <div className="p-4 bg-gray-50 rounded">
        <p className="text-sm text-gray-700 mb-2">You are not signed in. To change your password, please login first or use Forgot Password.</p>
        <div className="flex space-x-2">
          <a href="/forgot-password" className="text-sm text-primary underline">Forgot Password</a>
          <a href="/driver/login" className="text-sm text-gray-600 underline">Back to Login</a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">Current Password</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaLock className="h-5 w-5 text-black" />
          </div>
          <input
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="mt-1 block w-full border rounded p-2 pl-10 pr-10"
            required
          />
          <button type="button" onClick={() => setShowCurrentPassword(prev => !prev)} className="absolute right-3 top-3 text-gray-700 hover:text-gray-900 focus:outline-none">
            {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">New Password</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaLock className="h-5 w-5 text-black" />
          </div>
          <input
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="mt-1 block w-full border rounded p-2 pl-10 pr-10"
            required
          />
          <button type="button" onClick={() => setShowNewPassword(prev => !prev)} className="absolute right-3 top-3 text-gray-700 hover:text-gray-900 focus:outline-none">
            {showNewPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaLock className="h-5 w-5 text-black" />
          </div>
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="mt-1 block w-full border rounded p-2 pl-10 pr-10"
            required
          />
          <button type="button" onClick={() => setShowConfirmPassword(prev => !prev)} className="absolute right-3 top-3 text-gray-700 hover:text-gray-900 focus:outline-none">
            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          className={`px-4 py-2 rounded ${loading ? 'bg-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Change Password'}
        </button>
      </div>
    </form>
  );
};

export default ChangePassword;
